import { useState } from "preact/hooks"
import { Label } from "../../../components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { DataTable, InlineBar, Panel, Tile, column } from "../trade_review/trade_review_parts"
import { fmt_int, fmt_pct0, fmt_usd_short } from "../trade_review/trade_review_model"
import { PLAN_LISTS, coverage_row, fmt_date_range, plan_band, plan_row, promo_lift_review, unbilled_row } from "./promo_lift_review_model"
import { Fig } from "./promo_lift_review_parts"

const CRITICAL_COLOR = "#d03b3b"

const COVERAGE_COLUMNS: column<coverage_row>[] = [
  { header: "Customer", left: true, cell: (row: coverage_row) => row.customer },
  { header: "Promo deductions", cell: (row: coverage_row) => fmt_int(row.promo_ded) },
  { header: "Amount", cell: (row: coverage_row) => fmt_usd_short(row.promo_amt) },
  { header: "Matched", cell: (row: coverage_row) => fmt_int(row.matched_ded) },
  { header: "Matched amount", cell: (row: coverage_row) => fmt_usd_short(row.matched_amt) },
  { header: "Share matched", left: true, cell: (row: coverage_row) => <InlineBar share={row.matched_pct} label={fmt_pct0(row.matched_pct)} color={row.matched_pct < 0.5 ? CRITICAL_COLOR : undefined} /> },
]

const BAND_COLUMNS: column<plan_band>[] = [
  { header: "Billed against plan", left: true, cell: (row: plan_band) => row.band },
  { header: "Promotions", cell: (row: plan_band) => fmt_int(row.promos) },
  { header: "Planned", cell: (row: plan_band) => fmt_usd_short(row.planned) },
  { header: "Billed", cell: (row: plan_band) => fmt_usd_short(row.billed) },
]

const PLAN_COLUMNS: column<plan_row>[] = [
  { header: "Promotion", left: true, wrap: true, cell: (row: plan_row) => row.promo },
  { header: "Through", left: true, cell: (row: plan_row) => row.customer },
  { header: "Retailer", left: true, cell: (row: plan_row) => row.retailer },
  { header: "Dates", left: true, cell: (row: plan_row) => fmt_date_range(row.start, row.end) },
  { header: "Planned", cell: (row: plan_row) => fmt_usd_short(row.planned) },
  { header: "Billed", cell: (row: plan_row) => fmt_usd_short(row.billed) },
  { header: "Of plan", cell: (row: plan_row) => (row.ratio == null ? "" : fmt_pct0(row.ratio)) },
  { header: "Deductions", cell: (row: plan_row) => fmt_int(row.deductions) },
]

const UNBILLED_COLUMNS: column<unbilled_row>[] = [
  { header: "Promotion", left: true, wrap: true, cell: (row: unbilled_row) => row.promo },
  { header: "Through", left: true, cell: (row: unbilled_row) => row.customer },
  { header: "Retailer", left: true, cell: (row: unbilled_row) => row.retailer },
  { header: "Dates", left: true, cell: (row: unbilled_row) => fmt_date_range(row.start, row.end) },
  { header: "Planned", cell: (row: unbilled_row) => fmt_usd_short(row.planned) },
]

const LIST_LABELS: Record<string, string> = { Largest: "Largest billed", "Over plan": "Furthest over plan", "Under plan": "Furthest under plan, $20k+ planned" }

export function MatchTab({ data }: { data: promo_lift_review }) {
  const [list, set_list] = useState(PLAN_LISTS[0])
  const s = data.summary
  return (
    <div class="space-y-6">
      <div class="grid gap-4 sm:grid-cols-2">
        <Tile label="Matched promo spend" value={<Fig summary={s} field="matched_amt" as="usd_short" />} note={<><Fig summary={s} field="matched_ded" as="int" /> deductions, <Fig summary={s} field="customers" as="int" /> customers</>} />
        <Tile label="Finished promos, planned" value={<Fig summary={s} field="done_planned" as="usd_short" />} note={<><Fig summary={s} field="done_billed" as="usd_short" /> billed against them so far</>} />
      </div>
      <Panel title="How much promo spend is matched, by customer" note="Promo deductions are retailer promotions, distributor promotions and slotting invoiced since January 1, net of reversals.">
        <DataTable columns={COVERAGE_COLUMNS} rows={data.coverage} row_key={(row: coverage_row) => row.customer} />
      </Panel>
      <Panel title="Plan against billed, finished promotions" note="Promotions that ended by August 31, so billing has had two months to arrive.">
        <DataTable columns={BAND_COLUMNS} rows={data.plan_bands} row_key={(row: plan_band) => row.band} />
      </Panel>
      <Panel title="Promotions by billed spend">
        <div class="space-y-1">
          <Label>Show</Label>
          <Select value={list} onValueChange={set_list}>
            <SelectTrigger className="w-72"><SelectValue /></SelectTrigger>
            <SelectContent>{PLAN_LISTS.map((name: string) => <SelectItem key={name} value={name}>{LIST_LABELS[name]}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <DataTable columns={PLAN_COLUMNS} rows={data.promo_plan.filter((row: plan_row) => row.list === list)} row_key={(row: plan_row) => row.key} />
      </Panel>
      <Panel title="Finished in 2026 with nothing billed yet" note="Largest planned first. Either the retailer has not billed, or the deduction came in and is not yet matched.">
        <DataTable columns={UNBILLED_COLUMNS} rows={data.unbilled} row_key={(row: unbilled_row) => `${row.promo}|${row.start}`} />
      </Panel>
    </div>
  )
}
